import {
  Anchor,
  Badge,
  Box,
  Card,
  Flex,
  Image,
  Skeleton,
  Stack,
  Text,
  useMatches,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import classes from "./AuctionCard.module.css";
import AuctionCountdown from "./AuctionCountdown";

import auction1 from "@/assets/illustrations/auctions/auction-1.svg";
import auction2 from "@/assets/illustrations/auctions/auction-2.svg";
import auction3 from "@/assets/illustrations/auctions/auction-3.svg";
import auction4 from "@/assets/illustrations/auctions/auction-4.svg";
import auction5 from "@/assets/illustrations/auctions/auction-5.svg";
import auction6 from "@/assets/illustrations/auctions/auction-6.svg";
import auction7 from "@/assets/illustrations/auctions/auction-7.svg";
import auction8 from "@/assets/illustrations/auctions/auction-8.svg";
import auction9 from "@/assets/illustrations/auctions/auction-9.svg";
import auction10 from "@/assets/illustrations/auctions/auction-10.svg";
import auction11 from "@/assets/illustrations/auctions/auction-11.svg";
import auction12 from "@/assets/illustrations/auctions/auction-12.svg";
import { TextSerif } from "@/components/elements";
import { formatDate } from "@/utils/formatDate";

import type { Auction } from "@/types/auctions";
import type { FC } from "react";

/* TODO: assets 5,7 e 9 are currently missing */
const auctionIllustrations = [
  auction1,
  auction2,
  auction3,
  auction4,
  auction5,
  auction6,
  auction7,
  auction8,
  auction9,
  auction10,
  auction11,
  auction12,
];

export type AuctionCardProps = {
  auction?: Auction;
  index: number;
  onClick?: () => void;
  to?: string;
};

export const AuctionCard: FC<AuctionCardProps> = ({ auction, index, onClick, to }) => {
  const { t } = useTranslation();
  const isMobile = useMatches({ base: true, md: false });

  /* Constants */
  const height = isMobile ? 260 : 270;
  const showCountdown = auction?.status === "upcoming";
  const auctionDate = auction?.start_date || auction?.end_date;
  const illustration = auctionIllustrations[index % auctionIllustrations.length];
  const isClickable = !!auction && auction.num_cars > 0;
  const title = auction?.name || "";

  return (
    <Card
      h={height}
      p={0}
      className={auction?.id ? classes.card : undefined}
      onClick={isClickable ? onClick : undefined}
      style={{ position: "relative" }}
    >
      <Skeleton visible={!auction} h="100%">
        <Box pos="relative" p="sm">
          <Image src={illustration} w="100%" h={120} alt="" />

          {showCountdown && <AuctionCountdown auctionDate={auctionDate} />}
        </Box>

        <Stack justify="space-between" gap={0} p={isMobile ? "sm" : "md"}>
          <Flex justify="space-between" align="center">
            <TextSerif size="xs" style={{ flexShrink: 0 }}>
              {auctionDate ? formatDate(auctionDate) : ""}
            </TextSerif>
          </Flex>

          <Stack gap="md" pt="sm">
            <Box
              style={{
                minHeight: isMobile ? "48px" : "56px",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              {to && isClickable ? (
                <Anchor
                  component={Link}
                  onClick={(evt) => evt.stopPropagation()}
                  to={to}
                  size="lg"
                  lh="normal"
                  className={classes.clamp2}
                  title={title}
                >
                  {title}
                </Anchor>
              ) : (
                <Text size="lg" ta="left" lh="normal" title={title} className={classes.clamp2}>
                  {title}
                </Text>
              )}
            </Box>

            <Flex justify="space-between" gap="sm" pt="sm">
              {auction?.num_cars === 0 ? (
                <Badge variant="transparent" px={0} py="md" c="dimmed">
                  {t("common.dataAvailableSoon")}
                </Badge>
              ) : (
                <>
                  <Badge variant="light" p="md">
                    {t("components.Auctions.cars_on_sale", {
                      value: auction?.num_cars || 0,
                    })}
                  </Badge>

                  {auction?.place && (
                    <Badge variant="light-gray" style={{ maxWidth: "65%", overflow: "hidden" }}>
                      <Text component="span" fz="xs" truncate fw={600}>
                        {auction.place}
                      </Text>
                    </Badge>
                  )}
                </>
              )}
            </Flex>
          </Stack>
        </Stack>
      </Skeleton>
    </Card>
  );
};
